// Tiny, dependency-free SVG chart helpers used across Insights + Home.
// Each component is a pure SVG that scales to its container width.

import React from 'react';

// Sparkline — single-color filled line for trend KPI cards.
export function Sparkline({ values = [], color = '#3b82f6', height = 36, fillOpacity = 0.18 }) {
  if (!values.length) return <div style={{ height }} />;
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const w = 120, h = height;
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1 || 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return [x, y];
  });
  const linePath = pts.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(' ');
  const areaPath = `${linePath} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      <path d={areaPath} fill={color} fillOpacity={fillOpacity} />
      <path d={linePath} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {pts.length > 0 && <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill={color} />}
    </svg>
  );
}

// Donut — multi-segment ring chart with center label.
export function Donut({ segments = [], size = 140, thickness = 20, label, sublabel }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  const r = size / 2 - thickness / 2;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={r} stroke="#f4f4f5" strokeWidth={thickness} fill="none" />
        {total > 0 && segments.map((s, i) => {
          const len = (s.value / total) * circumference;
          const dasharray = `${len} ${circumference - len}`;
          const rotation = (offset / circumference) * 360 - 90;
          offset += len;
          return (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              stroke={s.color}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={dasharray}
              strokeDashoffset="0"
              strokeLinecap="butt"
              style={{ transform: `rotate(${rotation}deg)`, transformOrigin: `${cx}px ${cy}px` }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-['Outfit'] text-[22px] font-black text-zinc-900 tabular-nums leading-none">{label}</div>
        {sublabel && <div className="text-[10px] text-zinc-400 mt-1 uppercase tracking-wide font-semibold">{sublabel}</div>}
      </div>
    </div>
  );
}

// Horizontal bar chart with category labels — used for Priority distribution.
export function HBars({ rows = [], colorMap = {}, height = 22, max }) {
  const m = max || Math.max(1, ...rows.map(r => r.value || 0));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const pct = (r.value / m) * 100;
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[11px] text-zinc-600 capitalize w-16 flex-shrink-0">{r.label}</span>
            <div className="flex-1 bg-zinc-100 rounded-md overflow-hidden" style={{ height }}>
              <div
                className="h-full rounded-md flex items-center justify-end px-2 transition-all duration-500"
                style={{ width: `${pct}%`, background: colorMap[r.key] || r.color || '#6366f1' }}
              >
                {pct > 12 && <span className="text-[10px] font-bold text-white">{r.value}</span>}
              </div>
            </div>
            {pct <= 12 && <span className="text-[10px] font-bold text-zinc-700 tabular-nums w-6 text-right">{r.value}</span>}
          </div>
        );
      })}
    </div>
  );
}

// Stacked bar (committed vs completed) for sprint velocity.
// When showLegend=true, each project gets a unique color; otherwise default indigo.
const PROJECT_PALETTE = [
  ['#c7d2fe', '#4f46e5'], // indigo
  ['#fecdd3', '#e11d48'], // rose
  ['#a7f3d0', '#059669'], // emerald
  ['#fed7aa', '#ea580c'], // orange
  ['#bae6fd', '#0284c7'], // sky
  ['#ddd6fe', '#7c3aed'], // violet
  ['#fde68a', '#ca8a04'], // amber
  ['#bbf7d0', '#16a34a'], // green
];
export function VelocityBars({ data = [], colorByProject = false }) {
  if (!data.length) return <p className="text-center text-[12px] text-zinc-400 py-8">No completed sprints yet</p>;
  const max = Math.max(1, ...data.flatMap(d => [d.committed_points || 0, d.completed_points || 0]));
  const w = 600, h = 200, padX = 28, padY = 26;
  const slotW = (w - padX * 2) / data.length;
  const barW = Math.max(10, Math.min(28, slotW * 0.35));

  // Stable project → palette index map for per-project coloring.
  const projectMap = {};
  if (colorByProject) {
    let idx = 0;
    for (const d of data) {
      const pid = d.project?.project_id || 'none';
      if (!(pid in projectMap)) { projectMap[pid] = idx % PROJECT_PALETTE.length; idx++; }
    }
  }
  const colorsFor = (d) => {
    if (!colorByProject) return PROJECT_PALETTE[0];
    return PROJECT_PALETTE[projectMap[d.project?.project_id || 'none'] || 0];
  };

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[480px]">
        {/* Y grid */}
        {[0.25, 0.5, 0.75, 1].map(g => {
          const y = h - padY - g * (h - padY * 2);
          return (
            <g key={g}>
              <line x1={padX} x2={w - padX} y1={y} y2={y} stroke="#e4e4e7" strokeDasharray="2 4" />
              <text x={padX - 4} y={y + 3} fontSize="9" fill="#a1a1aa" textAnchor="end">{Math.round(g * max)}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const cx = padX + slotW * i + slotW / 2;
          const commH = ((d.committed_points || 0) / max) * (h - padY * 2);
          const compH = ((d.completed_points || 0) / max) * (h - padY * 2);
          const ratio = d.committed_points > 0 ? Math.round((d.completed_points / d.committed_points) * 100) : 0;
          const [light, dark] = colorsFor(d);
          const projectLabel = d.project?.key ? `[${d.project.key}] ` : '';
          return (
            <g key={d.sprint_id || i}>
              <rect x={cx - barW - 2} y={h - padY - commH} width={barW} height={commH} fill={light} rx="2.5">
                <title>{`${projectLabel}${d.name}\nCommitted: ${d.committed_points}`}</title>
              </rect>
              <rect x={cx + 2} y={h - padY - compH} width={barW} height={compH} fill={dark} rx="2.5">
                <title>{`${projectLabel}${d.name}\nCompleted: ${d.completed_points}\nRatio: ${ratio}%`}</title>
              </rect>
              <text x={cx} y={h - padY + 12} fontSize="9" fill="#71717a" textAnchor="middle">
                {(d.name || '').length > 10 ? (d.name || '').slice(0, 9) + '…' : d.name}
              </text>
              {d.project?.key && colorByProject && (
                <text x={cx} y={h - padY + 22} fontSize="8" fill="#a1a1aa" textAnchor="middle" fontFamily="monospace">{d.project.key}</text>
              )}
              {commH > 24 && <text x={cx} y={h - padY - commH - 3} fontSize="8" fill={dark} textAnchor="middle" fontWeight="700">{ratio}%</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Build a stable project → color pair lookup the page can render in legend.
export function projectPaletteFor(projects = []) {
  return projects.map((p, i) => ({ project: p, light: PROJECT_PALETTE[i % PROJECT_PALETTE.length][0], dark: PROJECT_PALETTE[i % PROJECT_PALETTE.length][1] }));
}

// Throughput line chart with two series (created vs completed).
export function ThroughputChart({ weeks = [] }) {
  if (!weeks.length) return <p className="text-center text-[12px] text-zinc-400 py-8">No throughput data</p>;
  const max = Math.max(1, ...weeks.flatMap(w => [w.created || 0, w.completed || 0]));
  const w = 600, h = 200, padX = 28, padY = 26;
  const slotW = (w - padX * 2) / Math.max(1, weeks.length - 1);
  const xy = (val, i) => [padX + slotW * i, h - padY - (val / max) * (h - padY * 2)];
  const linePath = (key, _color) => weeks.map((wk, i) => {
    const [px, py] = xy(wk[key] || 0, i);
    return (i === 0 ? `M ${px} ${py}` : `L ${px} ${py}`);
  }).join(' ');
  const areaPath = (key) => {
    const top = weeks.map((wk, i) => {
      const [px, py] = xy(wk[key] || 0, i);
      return (i === 0 ? `M ${px} ${py}` : `L ${px} ${py}`);
    }).join(' ');
    const last = padX + slotW * (weeks.length - 1);
    return `${top} L ${last} ${h - padY} L ${padX} ${h - padY} Z`;
  };
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[480px]">
        {[0.25, 0.5, 0.75, 1].map(g => {
          const y = h - padY - g * (h - padY * 2);
          return (
            <g key={g}>
              <line x1={padX} x2={w - padX} y1={y} y2={y} stroke="#e4e4e7" strokeDasharray="2 4" />
              <text x={padX - 4} y={y + 3} fontSize="9" fill="#a1a1aa" textAnchor="end">{Math.round(g * max)}</text>
            </g>
          );
        })}
        <path d={areaPath('completed')} fill="#10b981" fillOpacity="0.08" />
        <path d={linePath('completed', '#10b981')} stroke="#10b981" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d={linePath('created', '#3b82f6')} stroke="#3b82f6" strokeWidth="2" fill="none" strokeDasharray="3 3" strokeLinecap="round" strokeLinejoin="round" />
        {weeks.map((wk, i) => {
          const [cx, cy1] = xy(wk.completed || 0, i);
          const [, cy2] = xy(wk.created || 0, i);
          return (
            <g key={i}>
              <circle cx={cx} cy={cy1} r="3" fill="#10b981" />
              <circle cx={cx} cy={cy2} r="2.5" fill="#fff" stroke="#3b82f6" strokeWidth="1.5" />
              <text x={cx} y={h - padY + 12} fontSize="9" fill="#71717a" textAnchor="middle">{wk.label}</text>
              <title>{`Week of ${wk.label}\nCreated: ${wk.created}\nCompleted: ${wk.completed}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
